import { CODE_ENV_LIST, LOGIN_TOKEN_NAME, EMIT_EVENT, SSE_TYPE, isDev } from '@/common/constants/ConstCode';
import { Client } from '@stomp/stompjs';
import { chat } from '@/common/service/ApiList';
import { asFormData } from '@/common/utils/ServiceSupport';
import ServiceExec from '@/common/utils/ServiceExec';
import { scrollDown } from '@/views/chat/lobby/chatUtils';
import debounce from 'lodash/debounce';
import store from '@/store';
import {
  createLocalChatUiFixture,
  createLocalSentMessage,
  filterLocalChatUiFixtureRooms,
  findLocalChatUiFixtureRoom,
  isLocalChatUiFixtureEnabled,
  markLocalChatUiFixtureRoomRead,
  LOCAL_CHAT_UI_FIXTURE_IMAGE_PATH
} from '@/views/chat/__fixtures__/chatUiFixture';

const RESET = '\u001B[0m';
const FONT_GREEN = '\u001B[32m';
const subscribeUrl = (roomNo) => `/topic/${roomNo}`;
const sendUrl = (roomNo) => `/app/message/${roomNo}`;
const { create, getList, detail, chats, saveFile, disabledRoom, leaveRoom } = chat;

let client; // stomp client
let clientIdentity = null;
let subscription; // stomp subscription
let subscriptionIdentity = null;
let subscriptionRoomNo = null;
let subscriptionGeneration = 0;
let subscribeRequestId = 0;
let reconnectSubscriptionPromise = Promise.resolve(false);
let fetchRoomRequestId = 0;
let fetchChatsRequestId = 0;

const normalizeRoomNo = (roomNo) => Number(roomNo) || null;
const normalizeIdentity = ({ userNo, accessToken } = {}) => {
  const normalizedUserNo = Number(userNo) || null;
  if (!normalizedUserNo || typeof accessToken !== 'string' || !accessToken) return null;
  return Object.freeze({ userNo: normalizedUserNo, accessToken });
};
const getCanonicalIdentity = () =>
  normalizeIdentity({
    userNo: store.getters['user/getUserData']?.userNo,
    accessToken: store.getters['user/getAccessToken']
  });
const isSameIdentity = (left, right) =>
  !!left &&
  !!right &&
  left.userNo === right.userNo &&
  left.accessToken === right.accessToken;
const isCanonicalIdentity = (identity) => isSameIdentity(identity, getCanonicalIdentity());
const isStoredIdentity = (state, identity) => isSameIdentity(normalizeIdentity(state.header), identity);
const ALLOWED_BUBBLE_TYPES = new Set(['PLAIN', 'IMAGE', 'COPY', 'LEAVE']);
const normalizeChatType = (type) => (typeof type === 'string' ? type.toUpperCase() : '');
const isAllowedBubble = (message) => ALLOWED_BUBBLE_TYPES.has(normalizeChatType(message?.type));
const commitChatRecords = (commit, mutation, records, expectedRoomNo) => {
  const bubbles = [];
  let handled = false;
  for (const message of Array.isArray(records) ? records : [records]) {
    if (normalizeRoomNo(message?.roomNo) !== normalizeRoomNo(expectedRoomNo)) continue;
    const type = normalizeChatType(message?.type);
    if (type === 'READ') {
      commit('readAllMessages', message.userNo);
      handled = true;
    } else if (isAllowedBubble(message)) {
      bubbles.push(message);
      handled = true;
    }
  }
  if (bubbles.length > 0) commit(mutation, Array.isArray(records) ? bubbles : bubbles[0]);
  return handled;
};
const isDesiredRoom = (state, roomNo) => normalizeRoomNo(state.desiredRoomNo) === normalizeRoomNo(roomNo);
const isSubscribedRoom = (state, roomNo) =>
  normalizeRoomNo(state.subscribedRoomNo) === normalizeRoomNo(roomNo) &&
  normalizeRoomNo(subscriptionRoomNo) === normalizeRoomNo(roomNo);

const waitUntilConnected = async (expectedClient = client) => {
  while (client === expectedClient && !expectedClient?.connected) {
    await new Promise((resolve) => setTimeout(resolve, 100)); // 100ms 대기
  }
  return client === expectedClient && expectedClient?.connected;
};

const unsubscribeOwnedRoom = ({ state, commit }, expectedRoomNo = null) => {
  const actualRoomNo = normalizeRoomNo(subscriptionRoomNo ?? state.subscribedRoomNo);
  const expected = normalizeRoomNo(expectedRoomNo);
  if (expected && actualRoomNo && expected !== actualRoomNo) return false;

  const ownedSubscription = subscription;
  const ownedIdentity = subscriptionIdentity ?? clientIdentity;
  subscription = null;
  subscriptionIdentity = null;
  subscriptionRoomNo = null;
  subscriptionGeneration += 1;
  commit('clearSubscribedRoom');

  if (!ownedSubscription) return false;

  if (!ownedIdentity) return false;
  const { userNo, accessToken } = ownedIdentity;
  try {
    ownedSubscription.unsubscribe({
      roomNo: actualRoomNo,
      userNo: String(userNo),
      accessToken,
      channel: 'CHAT'
    });
  } catch (e) {
    console.warn('Chat unsubscribe failed for room :: ', actualRoomNo, e);
  }
  return true;
};

const subscribeDesiredRoom = async ({ state, commit }, roomNo, { force = false } = {}) => {
  const targetRoomNo = normalizeRoomNo(roomNo);
  const ownedClient = client;
  const identity = clientIdentity;
  if (
    !targetRoomNo ||
    !ownedClient?.connected ||
    !isDesiredRoom(state, targetRoomNo) ||
    !isCanonicalIdentity(identity) ||
    !isStoredIdentity(state, identity)
  ) {
    return false;
  }
  if (!force && subscription && isSubscribedRoom(state, targetRoomNo)) return true;

  const requestId = ++subscribeRequestId;
  if (subscription) unsubscribeOwnedRoom({ state, commit });
  else commit('clearSubscribedRoom');

  const { userNo, accessToken } = identity;
  await ServiceExec.patch(chat.read(targetRoomNo, userNo));
  if (
    requestId !== subscribeRequestId ||
    client !== ownedClient ||
    !ownedClient.connected ||
    !isDesiredRoom(state, targetRoomNo) ||
    !isCanonicalIdentity(identity) ||
    !isStoredIdentity(state, identity)
  ) {
    return false;
  }

  commit('readAllMessages', userNo);
  const callbackGeneration = ++subscriptionGeneration;
  const onMessage = async (message) => {
    if (
      callbackGeneration !== subscriptionGeneration ||
      client !== ownedClient ||
      !ownedClient.connected ||
      !isCanonicalIdentity(identity) ||
      !isStoredIdentity(state, identity) ||
      !isDesiredRoom(state, targetRoomNo) ||
      !isSubscribedRoom(state, targetRoomNo)
    ) {
      return;
    }

    let response;
    try {
      response = JSON.parse(message.body);
    } catch {
      return;
    }
    if (normalizeRoomNo(response?.roomNo) !== targetRoomNo) return;

    const { type } = response;
    if (normalizeChatType(type) === 'READ') {
      commit('readAllMessages', response.userNo);
    } else if (isAllowedBubble(response)) {
      console.warn('수신한 메세지', response);
      commit('appendChat', response);

      try {
        ownedClient.publish({
          destination: sendUrl(targetRoomNo),
          headers: {
            userNo: String(userNo),
            accessToken,
            channel: 'CHAT'
          },
          body: JSON.stringify({
            userName: state.meta.name,
            roomNo: targetRoomNo,
            userNo,
            content: '',
            type: 'READ',
            isRead: false
          })
        });
      } catch (e) {
        console.error('CHAT publish error :: ', e);
      }
    } else return;

    scrollDown('chat_box');
  };

  const nextSubscription = ownedClient.subscribe(subscribeUrl(targetRoomNo), onMessage, {
    userNo: String(userNo),
    accessToken,
    channel: 'CHAT'
  });
  if (
    requestId !== subscribeRequestId ||
    client !== ownedClient ||
    !isDesiredRoom(state, targetRoomNo) ||
    !isCanonicalIdentity(identity) ||
    !isStoredIdentity(state, identity)
  ) {
    nextSubscription.unsubscribe({
      roomNo: targetRoomNo,
      userNo: String(userNo),
      accessToken,
      channel: 'CHAT'
    });
    return false;
  }

  subscription = nextSubscription;
  subscriptionIdentity = identity;
  subscriptionRoomNo = targetRoomNo;
  commit('setSubscribedRoom', targetRoomNo);
  return true;
};

/**
 * stomp 객체 생성 및 초기화
 */
const stomp = {
  chatInitialize: async ({ commit, state }, suppliedIdentity = {}) => {
    const canonicalIdentity = getCanonicalIdentity();
    if (!canonicalIdentity || !isSameIdentity(normalizeIdentity(suppliedIdentity), canonicalIdentity)) {
      return false;
    }

    // LOCAL UI FIXTURE: retain header state without creating a STOMP client.
    if (isLocalChatUiFixtureEnabled()) {
      commit('setHeader', canonicalIdentity);
      return true;
    }

    if (client && !isSameIdentity(clientIdentity, canonicalIdentity)) {
      subscribeRequestId += 1;
      unsubscribeOwnedRoom({ state, commit });
      const staleClient = client;
      client = null;
      clientIdentity = null;
      await staleClient.deactivate();
    }

    commit('setHeader', canonicalIdentity);
    if (client?.connected && isSameIdentity(clientIdentity, canonicalIdentity)) return true;
    if (client?.active && isSameIdentity(clientIdentity, canonicalIdentity)) {
      const activeClient = client;
      await waitUntilConnected(activeClient);
      await Promise.resolve();
      await reconnectSubscriptionPromise;
      return true;
    }

    if (subscription) {
      unsubscribeOwnedRoom({ state, commit });
    }

    // 채팅 뉴마크 관련 시작
    if (!client?.active) {
      commit('setActiveCount', state.activeCount + 1);
    } else {
      commit('setActiveCount', 0);
    }
    // 채팅 뉴마크 관련 끝
    if (!client) {
      const identity = canonicalIdentity;
      let nextClient;
      nextClient = new Client({
        brokerURL: CODE_ENV_LIST.CHAT_SERVER_URL,
        connectHeaders: {
          userNo: identity.userNo,
          accessToken: identity.accessToken,
          tokenType: LOGIN_TOKEN_NAME,
          channel: 'CHAT'
        },
        reconnectDelay: 500,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        onConnect: () => {
          if (
            client !== nextClient ||
            !isSameIdentity(clientIdentity, identity) ||
            !isCanonicalIdentity(identity) ||
            !isStoredIdentity(state, identity)
          ) {
            return;
          }
          const desiredRoomNo = normalizeRoomNo(state.desiredRoomNo);
          reconnectSubscriptionPromise = desiredRoomNo
            ? subscribeDesiredRoom({ state, commit }, desiredRoomNo, { force: true })
            : Promise.resolve(false);
        },
        onDisconnect: () => {
          // console.log('Disconnected from Chat STOMP server');
        },
        onStompError: (frame) => {
          console.warn('Chat Broker reported error: ' + frame.headers['message']);
          console.warn('Chat Additional details: ' + frame.body);
        },
        debug: (str) => {
          // if (isDev) console.warn('CHAT debug : ' + str);
        }
      });
      client = nextClient;
      clientIdentity = identity;
    }

    const activeClient = client;
    activeClient.activate();

    await waitUntilConnected(activeClient);
    await Promise.resolve();
    await reconnectSubscriptionPromise;
    return client === activeClient && isSameIdentity(clientIdentity, canonicalIdentity);
  },

  disconnect: ({ state, commit }) => {
    if (isLocalChatUiFixtureEnabled()) {
      commit('clearDesiredRoom');
      commit('clearSubscribedRoom');
      commit('flushChatData');
      return;
    }

    subscribeRequestId += 1;
    unsubscribeOwnedRoom({ state, commit });
    commit('clearDesiredRoom');
    commit('flushChatData');

    if (client) client.deactivate();
    client = null;
    clientIdentity = null;
  },

  reconnect: async ({ state, commit, getters }) => {
    if (isLocalChatUiFixtureEnabled()) return true;

    console.log('Chat STOMP Reconnect - RoomNo :: ', state.desiredRoomNo);
    const canonicalIdentity = getCanonicalIdentity();
    if (!canonicalIdentity) return false;
    return await stomp.chatInitialize({ commit, state, getters }, canonicalIdentity);
  },

  /**
   * 채팅방 구독 (CHAT 채널)
   */
  chatRoomSubscribeRemote: async ({ state, commit, getters }, roomNo) => {
    const targetRoomNo = normalizeRoomNo(roomNo);
    if (!targetRoomNo) return false;

    commit('setDesiredRoom', targetRoomNo);
    const canonicalIdentity = getCanonicalIdentity();
    if (!canonicalIdentity) return false;
    const initialized = await stomp.chatInitialize(
      { state, commit, getters },
      canonicalIdentity
    );
    if (!initialized) return false;

    if (!client?.connected) {
      console.log('chatInitialize() in stomp.chatRoomSubscribe()');
      return isSubscribedRoom(state, targetRoomNo);
    }

    return subscribeDesiredRoom({ state, commit }, targetRoomNo);
  },

  wake: debounce(
    async ({ state, commit, getters }) => {
      if (isLocalChatUiFixtureEnabled()) return true;

      console.warn('Chat STOMP WAKE UP !!');
      return await stomp.reconnect({ state, commit, getters });
    },
    300,
    { leading: true }
  ),

  /*
  MEMO: 안드에서 이미지 업로드 시에도 실행됨
   */
  hibernate: debounce(async ({ state, commit, getters }) => {
    if (isLocalChatUiFixtureEnabled()) return;

    console.warn('Chat STOMP Hibernate');
    if (!client?.connected) return;

    let imageUploadState = getters['getImageUploadState'];
    if (imageUploadState) return;

    if (subscription) {
      console.warn('Chat unsubscribe() in hibernate()');
      unsubscribeOwnedRoom({ state, commit });

      await client.deactivate();
      client = null;
      clientIdentity = null;
    }
  }, 300),

  unSubscribe: async ({ state, commit }, roomNo) => {
    const targetRoomNo = normalizeRoomNo(roomNo);
    subscribeRequestId += 1;
    if (!targetRoomNo || isDesiredRoom(state, targetRoomNo)) commit('clearDesiredRoom');
    unsubscribeOwnedRoom({ state, commit }, targetRoomNo);
    commit('flushChatData');
    if (isLocalChatUiFixtureEnabled()) return;
  },

  /**
   * 채팅 메시지 전송
   */
  send: async ({ state, commit }, { roomNo, msg, file }) => {
    const targetRoomNo = normalizeRoomNo(roomNo);
    if (!targetRoomNo || !isDesiredRoom(state, targetRoomNo)) return false;
    if (normalizeRoomNo(state.subscribedRoomNo) !== targetRoomNo) return false;
    const canonicalIdentity = getCanonicalIdentity();
    if (!canonicalIdentity || !isStoredIdentity(state, canonicalIdentity)) return false;

    if (isLocalChatUiFixtureEnabled()) {
      // LOCAL UI FIXTURE: append an own bubble directly to the existing chat state.
      const userData = store.getters['user/getUserData'] ?? {};
      const localBubble = createLocalSentMessage({
        roomNo: targetRoomNo,
        currentUser: {
          userNo: userData.userNo ?? state.header.userNo ?? 0,
          name: userData.name ?? state.meta.name
        },
        content: msg,
        file: file ?? null,
        chats: state.chats.data
      });

      commit('appendChat', localBubble);
      return true;
    }

    if (
      !client?.connected ||
      !isSubscribedRoom(state, targetRoomNo) ||
      !isSameIdentity(clientIdentity, canonicalIdentity)
    ) {
      return false;
    }
    const boundClient = client;
    const { userNo, accessToken } = clientIdentity;
    const { name } = state.meta;

    let container = {
      userName: name,
      roomNo: targetRoomNo,
      userNo,
      content: msg,
      type: file ? 'IMAGE' : 'PLAIN',
      file,
      isRead: false
    };

    try {
      boundClient.publish({
        destination: sendUrl(targetRoomNo),
        headers: {
          userNo: String(userNo),
          accessToken: accessToken,
          channel: 'CHAT'
        },
        body: JSON.stringify(container)
      });
    } catch (e) {
      console.error('CHAT SEND ERROR', e);
      return false;
    }
    return true;
  },

  leave: ({ state, commit }) => {
    console.log('CHAT LEAVE');
    commit('setImageUploadState', false);
    if (isLocalChatUiFixtureEnabled()) return;
    const roomNo = normalizeRoomNo(state.desiredRoomNo);
    const canonicalIdentity = getCanonicalIdentity();
    if (
      !client?.connected ||
      !canonicalIdentity ||
      !isStoredIdentity(state, canonicalIdentity) ||
      !isSameIdentity(clientIdentity, canonicalIdentity) ||
      !roomNo ||
      !isSubscribedRoom(state, roomNo)
    ) {
      return;
    }

    const boundClient = client;
    const { userNo, accessToken } = clientIdentity;
    unsubscribeOwnedRoom({ state, commit }, roomNo);
    const { name } = state.meta;

    let container = {
      name,
      roomNo,
      userNo,
      content: '',
      type: 'LEAVE',
      isRead: false
    };
    try {
      boundClient.publish({
        destination: sendUrl(roomNo),
        headers: {
          userNo: userNo + '',
          accessToken: accessToken,
          channel: 'CHAT'
        },
        body: JSON.stringify(container)
      });
    } catch (e) {
      console.error('CHAT LEAVE ERROR', e);
    }
  }
};

const { chatRoomSubscribeRemote, ...stompActions } = stomp;

const chatRoomSubscribe = ({ state, commit, getters }, roomNo) => {
  const targetRoomNo = normalizeRoomNo(roomNo);
  if (!targetRoomNo) return false;
  commit('setDesiredRoom', targetRoomNo);

  if (isLocalChatUiFixtureEnabled()) {
    // LOCAL UI FIXTURE: synchronously persist and publish the room's read state without transport.
    if (!markLocalChatUiFixtureRoomRead(targetRoomNo)) return false;

    const userData = store.getters['user/getUserData'] ?? {};
    const header = getters['getHeader'];
    const fixture = findLocalChatUiFixtureRoom(targetRoomNo, userData);
    if (!fixture.room) return false;

    commit('selectRoom', fixture.room);
    commit('setSubscribedRoom', targetRoomNo);
    commit('readAllMessages', Number(userData.userNo ?? header.userNo ?? 0));
    commit('setRoomUnreadBubbleCount', { roomNo: targetRoomNo, count: 0 });
    commit('setBubbleCounts', fixture.bubbleCounts);
    return true;
  }

  return chatRoomSubscribeRemote({ state, commit, getters }, targetRoomNo);
};

const apiActions = {
  CREATE_CHATROOM: async ({ state, commit }, params = { roomType, userNo, storeNo }) => {
    // LOCAL UI FIXTURE: never create a room through the REST API while the fixture gate is active.
    if (isLocalChatUiFixtureEnabled()) return 0;

    const { status, data } = await ServiceExec.post(create, params);
    if (status !== ServiceExec.status.OK) return 0;
    commit('selectRoom', data);
    return data.roomNo;
  },
  FETCH_CHATROOM: async ({ commit, state }, params = {}) => {
    const requestedRoomNo = normalizeRoomNo(params?.roomNo);
    if (isLocalChatUiFixtureEnabled()) {
      // LOCAL UI FIXTURE: resolve direct room URLs from local source data.
      const userData = store.getters['user/getUserData'] ?? {};
      const { room } = findLocalChatUiFixtureRoom(params?.roomNo, userData);
      if (requestedRoomNo && state.desiredRoomNo && !isDesiredRoom(state, requestedRoomNo)) return;
      commit('selectRoom', room ?? undefined);
      return room;
    }

    const requestId = ++fetchRoomRequestId;
    const { status, data } = await ServiceExec.get(detail, params);
    if (requestId !== fetchRoomRequestId) return;
    if (requestedRoomNo && state.desiredRoomNo && !isDesiredRoom(state, requestedRoomNo)) return;

    if (status === ServiceExec.status.OK) {
      if (requestedRoomNo && normalizeRoomNo(data?.roomNo) !== requestedRoomNo) return;
      commit('selectRoom', data);
    } else commit('selectRoom');
    // commit('setSelectedRoom', roomNo ?? 0);
    // commit('setSelectedRoomUser', profiles ?? []);
  },
  FETCH_CHATROOMS: async ({ commit, getters }) => {
    const { page, limit, totalPage, roomType, orderTab, nextId } = getters['getRoomPageInfo'];
    const headerData = getters['getHeader'];

    if (isLocalChatUiFixtureEnabled()) {
      // LOCAL UI FIXTURE: re-materialize retained state for every lobby fetch and terminate paging.
      const userData = store.getters['user/getUserData'] ?? {};
      const fixture = createLocalChatUiFixture(userData);
      const fixtureRooms = filterLocalChatUiFixtureRooms(fixture.rooms, { roomType, orderTab });

      commit('setRoomPage', 2);
      commit('setRoomPageNextId', 0);
      commit('setRoomTotalPage', 1);
      commit('replaceRoomData', fixtureRooms);
      commit('setBubbleCounts', fixture.bubbleCounts);
      return fixtureRooms;
    }

    let userNo = headerData.userNo;
    if (!userNo || userNo === 0) {
      const userData = store.getters['user/getUserData'];
      userNo = userData.userNo;
    }

    if (nextId == 0) return;
    const params = { page, limit, roomType, userNo, orderTab, nextId };

    // if (page > totalPage) return;
    const { status, data } = await ServiceExec.get(getList, params);

    if (status === 200) {
      const { pageInfo, list } = data;
      commit('setRoomPage', page + 1);
      commit('setRoomPageNextId', pageInfo.nextId);
      commit('setRoomTotalPage', pageInfo?.totalPage ?? 2);
      commit('setRoomData', list ?? []);
    }
  },

  fetchChatList: async ({ state, commit }, payload = {}) => {
    if (isLocalChatUiFixtureEnabled()) {
      // LOCAL UI FIXTURE: hydrate once and never fall through to the chat-list API.
      const roomNo = normalizeRoomNo(payload?.roomNo ?? state.desiredRoomNo);
      if (!roomNo || state.chats.page > 1 || state.chats.data.length > 0) return;

      const userData = store.getters['user/getUserData'] ?? {};
      const { room, chats: fixtureChats } = findLocalChatUiFixtureRoom(roomNo, userData);
      if (!room || !isDesiredRoom(state, roomNo)) return;

      commit('setChatTotalPage', 1);
      commit('setChatPage', 2);
      commitChatRecords(
        commit,
        'setChatData',
        fixtureChats.map((message) => ({ ...message, roomNo: message.roomNo ?? roomNo })),
        roomNo
      );
      return fixtureChats;
    }

    const requestId = ++fetchChatsRequestId;
    const roomNo = normalizeRoomNo(payload?.roomNo ?? state.desiredRoomNo);
    const { page, limit, totalPage } = state.chats;

    if (!roomNo) return;
    if (page > totalPage) return;

    const { status, data } = await ServiceExec.get(chats(roomNo), {
      page,
      limit
    });
    if (requestId !== fetchChatsRequestId) return;
    if (!isDesiredRoom(state, roomNo)) return;
    if (status !== ServiceExec.status.OK || !data) return;

    const { pageInfo, list } = data;

    commit('setChatTotalPage', pageInfo.totalPage ?? 2);
    commit('setChatPage', page + 1);
    commitChatRecords(
      commit,
      'setChatData',
      (list ?? []).map((message) => ({ ...message, roomNo: message.roomNo ?? roomNo })),
      roomNo
    );
  },

  UPLOAD_IMAGE: async ({ state, commit }, file) => {
    if (isLocalChatUiFixtureEnabled()) {
      return {
        status: 200,
        data: {
          originalFileName: file?.name ?? 'gimchi.png',
          storedFileName: '',
          fixtureStaticPath: LOCAL_CHAT_UI_FIXTURE_IMAGE_PATH
        }
      };
    }

    return await ServiceExec.multiPost(saveFile, asFormData({ file }));
  },

  FETCH_BUBBLE_COUNT: async ({ commit }, userNo) => {
    if (isLocalChatUiFixtureEnabled()) {
      // LOCAL UI FIXTURE: derive every badge from the retained room unread values.
      const userData = store.getters['user/getUserData'] ?? {};
      const bubbleCounts = createLocalChatUiFixture(userData).bubbleCounts;
      commit('setBubbleCounts', bubbleCounts);
      return bubbleCounts;
    }

    const { status, data } = await ServiceExec.get(chat.bubbleCount(userNo), {}, { notLoading: true });
    if (status === 200) commit('setBubbleCounts', data);
  },

  DISABLE_CHAT_ROOM: async ({ state, commit }) => {
    if (isLocalChatUiFixtureEnabled()) {
      commit('selectRoom', { ...state.selectedRoom, isChattable: false });
      return true;
    }

    const { roomNo } = state.selectedRoom;
    const { status, data } = await ServiceExec.patch(disabledRoom(roomNo));

    const disabled = Object.assign({}, state.selectedRoom);
    disabled.isChattable = false;
    if (data) commit('setSelectedRoom', disabled);
  },
  LEAVE_CHAT_ROOM: async ({ state, commit, rootGetters }) => {
    if (isLocalChatUiFixtureEnabled()) return true;

    const { roomNo } = state.selectedRoom;
    const { userNo } = rootGetters['user/getUserData'];
    const { status, data } = await ServiceExec.patch(leaveRoom(roomNo, userNo));
    return data;
  },
  fetchLastChat: async ({ state, getters, commit }) => {
    if (isLocalChatUiFixtureEnabled()) return;

    const roomNo = normalizeRoomNo(state.desiredRoomNo);
    const chats = getters['getChats'];

    if (!roomNo || chats.length <= 0) return;
    const { bubbleNo } = chats[chats.length - 1];
    const { status, data } = await ServiceExec.get(chat.loadLast(roomNo, bubbleNo));

    if (!isDesiredRoom(state, roomNo)) return;
    if (status !== ServiceExec.status.OK) return;
    if (
      commitChatRecords(commit, 'appendChat', { ...data, roomNo: data?.roomNo ?? roomNo }, roomNo)
    ) {
      scrollDown('chat_box');
    }
  }
};

export const actions = {
  ...stompActions,
  ...apiActions,
  chatRoomSubscribe
};

export default actions;
